import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../../components/CustomTableWithPagination/GlobalTable';
import FromAndToDatePicker from '../../../../components/mui/FromAndToDatePicker';
import GlobalSelectFieldWithoutFormikNew from '../../../../components/mui/GlobalSelectFieldWithoutFormikNew';
import ReplacementStatCard from './components/ReplacementStatCard';
import {
  exportTutorReplacements,
  getTutorReplacementProjects,
  getTutorReplacements,
} from '../../../../redux/features/crdMigrationSlice';
import { tutorReplacementsTableHeader } from './utils/tutorReplacementsTableHeader';

// Categories are worked out on the backend from the unmap reason, see getTutorReplacements
const REASON_FILTERS = [
  { id: 'all', name: 'All' },
  { id: 'tutor', name: 'Tutor related' },
  { id: 'parent', name: 'Parent / Student related' },
  { id: 'other', name: 'Other (mistakenly assigned, classes stopped/completed)' },
];

const ALL_PROJECTS = { id: 'all', name: 'All' };

// `key` is the matching field in meta.summary
const STAT_CARDS = [
  { key: 'total', title: 'Total Replacements', icon: 'mdi:account-switch', color: { from: '#4F46E5', to: '#7C3AED' } },
  { key: 'tutor', title: 'Tutor Related', icon: 'mdi:human-male-board', color: { from: '#DC2626', to: '#F43F5E' } },
  {
    key: 'parent',
    title: 'Parent / Student Related',
    icon: 'mdi:human-male-child',
    color: { from: '#0284C7', to: '#06B6D4' },
  },
  { key: 'other', title: 'Other', icon: 'mdi:dots-horizontal-circle-outline', color: { from: '#EA580C', to: '#F59E0B' } },
];

export default function TutorReplacements() {
  const dispatch = useDispatch();
  const { loading, data, meta } = useSelector((state) => state.crdMigration.tutorReplacementsTable);

  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [reason, setReason] = useState({ id: 'all', value: 'All' });
  const [projects, setProjects] = useState([]);
  // null until the user's own project is known, so the table is not loaded for all projects first
  const [project, setProject] = useState(null);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    sort: { field: '', direction: 'asc' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  // a filter change must not leave the table on a page that no longer exists
  const resetPage = () => setTableState((prev) => ({ ...prev, page: 1 }));

  useEffect(() => {
    const promise = dispatch(getTutorReplacementProjects());
    promise.then((action) => {
      // aborted when the page unmounts (or React re-runs the effect) before the list arrives
      if (action.meta?.aborted) return;
      const list = action.payload?.data?.projects ?? [];
      const own = list.find((item) => item.id === action.payload?.data?.defaultProjectId);
      setProjects(list);
      setProject(own ? { id: own.id, value: own.name } : { id: ALL_PROJECTS.id, value: ALL_PROJECTS.name });
    });
    return () => promise.abort();
  }, [dispatch]);

  const dispatchParams = useMemo(
    () => ({
      page: tableState?.page,
      limit: tableState?.pageSize,
      search: tableState?.search,
      from: fromDate,
      to: toDate,
      reason: reason?.id,
      projectId: project?.id === ALL_PROJECTS.id ? '' : project?.id ?? '',
    }),
    [tableState?.page, tableState?.pageSize, tableState?.search, fromDate, toDate, reason, project],
  );

  useEffect(() => {
    if (!project) return undefined;
    // a newer filter change cancels the request still in flight, so a slower older response can't overwrite it
    const promise = dispatch(getTutorReplacements(dispatchParams));
    return () => promise.abort();
  }, [dispatch, dispatchParams, project]);

  // GlobalTable is memoised, so props that are rebuilt on every render would re-render the whole table
  const exportParams = useMemo(() => ({ ...dispatchParams, page: 1, limit: 'no' }), [dispatchParams]);
  const projectOptions = useMemo(() => [ALL_PROJECTS, ...projects], [projects]);

  // follows the project, date range and search; the reason filter only narrows the table,
  // so the cards keep showing the full breakdown
  const summary = meta?.summary;
  const statCards = useMemo(() => STAT_CARDS.map((card) => ({ ...card, total: summary?.[card.key] ?? 0 })), [summary]);

  const tableLoading = loading || !project;

  return (
    <GlobalWrapper title={'Tutor Replacements'}>
      <Stack gap={2}>
        <Grid container spacing={1.5}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={3} md={2.4} key={card.key}>
              <ReplacementStatCard
                title={card.title}
                total={tableLoading ? '…' : card.total}
                icon={card.icon}
                color={card.color}
              />
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <GlobalSelectFieldWithoutFormikNew
            data={projectOptions}
            keys={{ itemId: 'id', itemValue: 'name' }}
            label={'Project'}
            selectedDetails={project}
            setDirectChange={(value) => {
              setProject(value);
              resetPage();
            }}
            disabled={tableLoading}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
          />
          <FromAndToDatePicker
            disabled={tableLoading}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={(date) => {
              setFromDate(date);
              resetPage();
            }}
            setToDate={(date) => {
              setToDate(date);
              resetPage();
            }}
          />
          <GlobalSelectFieldWithoutFormikNew
            data={REASON_FILTERS}
            keys={{ itemId: 'id', itemValue: 'name' }}
            label={'Reason'}
            selectedDetails={reason}
            setDirectChange={(value) => {
              setReason(value);
              resetPage();
            }}
            disabled={tableLoading}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />
        </Stack>
      </Stack>
      <Box mt={1}>
        <GlobalTable
          rowUniqueId="slNo"
          headers={tutorReplacementsTableHeader}
          rows={data}
          tableState={tableState}
          setTableState={setTableState}
          loading={tableLoading}
          meta={meta}
          exportApi={exportTutorReplacements}
          exportParams={exportParams}
        />
      </Box>
    </GlobalWrapper>
  );
}
